import React from 'react';
import ReactPlayer from 'react-player';
import { Grid, Paper, Tab, Tabs } from '@mui/material';
import { Text } from '@innovyze/stylovyze';
import { t } from 'i18next';
import { MediaInspections, PlayerProgress } from '@Types';
import missingImg from '../ObservationPhoto/missing.png';

export interface VideoPlayerWithTabsProps {
	videoItems: MediaInspections[];
	selectedIndex: number;
	onTabChange: (index: number) => void;
	isLoading: boolean;
	keepPlaying: boolean;
	onProgress: (progress: PlayerProgress) => void;
	onEnded: () => void;
	onPlayerRef: (player: ReactPlayer | null) => void;
	uploadSlot?: React.ReactNode;
}

const NoVideoPlaceholder = () => (
	<Grid xs={12} item>
		<Paper
			id="NoVideoPlayer"
			style={{
				height: '380px',
				margin: '1rem',
				alignContent: 'center',
				backgroundColor: '#F1F4F4',
			}}>
			<div
				style={{
					height: '200px',
					width: '200px',
					margin: '0 auto',
					backgroundPosition: 'center',
					backgroundSize: 'contain',
					backgroundRepeat: 'no-repeat',
					backgroundImage: `url(${missingImg})`,
				}}
			/>
		</Paper>
	</Grid>
);

export const VideoPlayerWithTabs = ({
	videoItems,
	selectedIndex,
	onTabChange,
	isLoading,
	keepPlaying,
	onProgress,
	onEnded,
	onPlayerRef,
	uploadSlot,
}: VideoPlayerWithTabsProps) => {
	if (videoItems.length === 0) {
		if (!uploadSlot) return null;
		return (
			<Grid xs={12} item style={{ padding: '15px' }}>
				{uploadSlot}
			</Grid>
		);
	}

	const current = videoItems[selectedIndex];

	const statusCaption = () => {
		if (!current) return null;
		if (current.mediaTranscodeStatus === 'Ingest') {
			return (
				<Grid xs={12} item style={{ textAlign: 'center' }}>
					<Text variant="caption-medium">
						{t('Media Transcoding In Progress')}
					</Text>
				</Grid>
			);
		}
		if (current.mediaStatus === 'ready' && !current.videoLocation) {
			return (
				<Grid xs={12} item style={{ textAlign: 'center' }}>
					<Text variant="caption-medium">
						{t('Media Import In Progress')}
					</Text>
				</Grid>
			);
		}
		return null;
	};

	const playerPanel = () => {
		if (current?.mediaStatus === 'missing_media') {
			return <NoVideoPlaceholder />;
		}
		return (
			<Grid xs={12} item>
				<Paper
					id="VideoPlayer"
					style={{
						height: '380px',
						margin: '1rem',
						backgroundColor: '#F1F4F4',
					}}>
					<ReactPlayer
						ref={(p: ReactPlayer) => onPlayerRef(p)}
						height="100%"
						width="100%"
						controls={true}
						url={
							!isLoading && current?.videoLocation
								? current.videoLocation
								: ''
						}
						playing={keepPlaying}
						onProgress={onProgress}
						onEnded={onEnded}
					/>
				</Paper>
			</Grid>
		);
	};

	return (
		<>
			{videoItems.length >= 2 && selectedIndex < videoItems.length && (
				<Grid xs={12} item>
					<Tabs
						value={selectedIndex}
						onChange={(_: React.SyntheticEvent, v: number) =>
							onTabChange(v)
						}>
						{videoItems.map((item, i) => (
							<Tab
								key={item.mediaId ?? i}
								label={`${t('Video')} ${i + 1}`}
							/>
						))}
					</Tabs>
				</Grid>
			)}
			{playerPanel()}
			{statusCaption()}
		</>
	);
};
